import { apiClient, ApiResponse } from './apiClient';

// Product interfaces matching API structure
export interface ProductCategory {
  id: number;
  name: string;
  name_ar: string;
  icon?: string;
}

export interface ProductBrand {
  id: string;
  name: string;
  name_ar: string;
  logo_url?: string;
}

export interface Product {
  id: string;
  name: string;
  name_ar: string;
  slug: string;
  price: number;
  discount_price: number | null;
  stock: number;
  sku: string;
  barcode: string;
  status: string;
  is_deleted: boolean;
  is_featured: boolean;
  thumbnail_url: string | null;
  category?: ProductCategory;
  brand?: ProductBrand;
  createdAt: string;
  updatedAt: string;
}

export interface ProductDetails extends Omit<Product, 'category' | 'brand'> {
  description: string;
  description_ar: string;
  price: string; // API returns as string for details
  category_id: number;
  brand_id: string;
  images: string; // JSON string array
  rating: string;
  Category: ProductCategory;
  Brand: ProductBrand;
}

export interface ProductFilters {
  page?: number; // Required, default: 1
  limit?: number; // Required, default: 20, max: 100
  sort?: string; // Optional: price_asc, price_desc, newest, popular
  status?: string; // Optional: active, inactive
  is_deleted?: string; // Optional: true/false
  category_id?: string; // Optional: Filter by category ID
  brand_id?: string; // Optional: Filter by brand ID
  min_price?: string; // Optional: Minimum price
  max_price?: string; // Optional: Maximum price
  low_stock?: string; // Optional: true/false
  q?: string; // Optional: Search by name, name_ar, or SKU
}

export interface ProductPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ProductListResponse {
  items: Product[];
  pagination: ProductPagination;
}

export interface ProductsResponse {
  items: Product[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export class ProductService {
  private baseUrl = '/admin/products';

  // Upload product image (POST /admin/products/upload-image)
  async uploadImage(file: File, onProgress?: (pct: number) => void): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('image', file);
    return apiClient.upload<{ url: string }>(`${this.baseUrl}/upload-image`, formData, onProgress);
  }

  // Get products with filters (GET /admin/products)
  async getProducts(filters: ProductFilters = {}): Promise<ApiResponse<ProductsResponse>> {
    const params = new URLSearchParams();
    
    // Required params
    const page = filters.page || 1;
    const limit = Math.min(filters.limit || 20, 100); // Max 100 per API spec
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Optional filters
    if (filters.q) {
      params.append('q', filters.q);
    }
    if (filters.sort) {
      params.append('sort', filters.sort);
    }
    if (filters.status) {
      params.append('status', filters.status);
    }
    if (filters.is_deleted !== undefined) {
      params.append('is_deleted', filters.is_deleted);
    }
    if (filters.category_id) {
      params.append('category_id', filters.category_id);
    }
    if (filters.brand_id) {
      params.append('brand_id', filters.brand_id);
    }
    if (filters.min_price) {
      params.append('min_price', filters.min_price);
    }
    if (filters.max_price) {
      params.append('max_price', filters.max_price);
    }
    if (filters.low_stock !== undefined) {
      params.append('low_stock', filters.low_stock);
    }
    
    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    try {
      // API returns: {status: boolean, message: string, data: {items: [], pagination: {}}}
      const response = await apiClient.get<any>(url);

      if (response.success && response.data) {
        // response.data contains the API's data object: {items: [], pagination: {}}
        const apiData = response.data;
        const apiItems = apiData.items || [];
        const pagination = apiData.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          totalPages: 0
        };

        const listResponse: ProductsResponse = {
          items: apiItems,
          total: pagination.total,
          page: pagination.page,
          pageSize: pagination.limit,
          totalPages: pagination.totalPages,
        };

        return {
          success: true,
          data: listResponse,
          message: response.message || 'Products fetched successfully'
        };
      }

      // Return empty response if failed
      return {
        success: false,
        data: {
          items: [],
          total: 0,
          page: 1,
          pageSize: limit,
          totalPages: 0
        },
        message: response.message || 'Failed to fetch products'
      };
    } catch (error: any) {
      console.error('Error fetching products:', error);
      return {
        success: false,
        data: {
          items: [],
          total: 0,
          page: 1,
          pageSize: limit,
          totalPages: 0
        },
        message: error?.message || 'Error fetching products'
      };
    }
  }

  // Get product by ID (GET /admin/products/{id})
  async getProductById(id: string): Promise<ApiResponse<ProductDetails>> {
    try {
      const response = await apiClient.get<any>(`${this.baseUrl}/${id}`);
      
      if (response.success && response.data) {
        const apiData = response.data;
        return {
          success: true,
          data: apiData,
          message: response.message || 'Product fetched successfully'
        };
      }

      return {
        success: false,
        data: undefined,
        message: response.message || 'Failed to fetch product'
      };
    } catch (error: any) {
      console.error('Error fetching product:', error);
      return {
        success: false,
        data: undefined,
        message: error?.message || 'Error fetching product'
      };
    }
  }

  // Create product (POST /admin/products)
  async createProduct(data: CreateProductRequest): Promise<ApiResponse<ProductDetails>> {
    try {
      console.log('🔧 Creating product with data:', JSON.stringify(data, null, 2));
      const response = await apiClient.post<ProductDetails>(this.baseUrl, data);
      console.log('🔧 Create product response:', response);
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Product created successfully'
        };
      }

      return {
        success: false,
        data: undefined,
        message: response.message || 'Failed to create product'
      };
    } catch (error: any) {
      console.error('❌ Error creating product:', error);
      console.error('❌ Error details:', error?.response?.data);
      return {
        success: false,
        data: undefined,
        message: error?.response?.data?.message || error?.message || 'Error creating product'
      };
    }
  }

  // Update product (PUT /admin/products/{id})
  async updateProduct(id: string, data: UpdateProductRequest): Promise<ApiResponse<ProductDetails>> {
    try {
      console.log('🔧 Updating product:', id, 'with data:', JSON.stringify(data, null, 2));
      const response = await apiClient.put<ProductDetails>(`${this.baseUrl}/${id}`, data);
      console.log('🔧 Update product response:', response);
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Product updated successfully'
        };
      }

      return {
        success: false,
        data: undefined,
        message: response.message || 'Failed to update product'
      };
    } catch (error: any) {
      console.error('❌ Error updating product:', error);
      console.error('❌ Error details:', error?.response?.data);
      return {
        success: false,
        data: undefined,
        message: error?.response?.data?.message || error?.message || 'Error updating product'
      };
    }
  }

  // Delete product (DELETE /admin/products/{id})
  async deleteProduct(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete<void>(`${this.baseUrl}/${id}`);
      return response;
    } catch (error: any) {
      console.error('Error deleting product:', error);
      return {
        success: false,
        data: undefined,
        message: error?.message || 'Error deleting product'
      };
    }
  }

  // Update product status (PUT /admin/products/{id}/status)
  async updateProductStatus(id: string, status: 'active' | 'inactive'): Promise<ApiResponse<Product>> {
    try {
      const response = await apiClient.put<Product>(`${this.baseUrl}/${id}/status`, { status });
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Product status updated successfully'
        };
      }

      return {
        success: false,
        data: undefined,
        message: response.message || 'Failed to update product status'
      };
    } catch (error: any) {
      console.error('Error updating product status:', error);
      return {
        success: false,
        data: undefined,
        message: error?.message || 'Error updating product status'
      };
    }
  }

  // Update product stock (PUT /admin/products/{id}/stock)
  async updateProductStock(id: string, stock: number): Promise<ApiResponse<Product>> {
    try {
      const response = await apiClient.put<Product>(`${this.baseUrl}/${id}/stock`, { stock });
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Product stock updated successfully'
        };
      }

      return {
        success: false,
        data: undefined,
        message: response.message || 'Failed to update product stock'
      };
    } catch (error: any) {
      console.error('Error updating product stock:', error);
      return {
        success: false,
        data: undefined,
        message: error?.message || 'Error updating product stock'
      };
    }
  }
}

// Product create/update request interfaces
export interface CreateProductRequest {
  name: string;
  name_ar: string;
  description: string;
  description_ar: string;
  price: number;
  discount_price?: number | null;
  stock: number;
  sku: string;
  barcode: string;
  category_id: number;
  brand_id: string;
  status: string;
  is_featured: boolean;
  thumbnail_url?: string | null;
  images?: string[];
}

export interface UpdateProductRequest {
  name?: string;
  name_ar?: string;
  description?: string;
  description_ar?: string;
  price?: number;
  discount_price?: number | null;
  stock?: number;
  sku?: string;
  barcode?: string;
  category_id?: number;
  brand_id?: string;
  status?: string;
  is_featured?: boolean;
  thumbnail_url?: string | null;
  images?: string[];
}

// Export singleton instance
export const productService = new ProductService();
export default productService;

